/**
 * Calculate bookmark width based on text content
 * Only extends beyond anchor if text needs more space
 * @param {string} label - Phase label text
 * @param {number} duration - Duration in days
 * @param {number} anchorWidth - Anchor width (original phase rect width)
 * @returns {number} Calculated bookmark width (max of anchor width or text width, capped at 200px)
 */
export const calculateBookmarkWidth = (label, duration, anchorWidth) => {
  // Estimate based on text length (approximate, no DOM measurement needed)
  const labelWidth = label.length * 5.5  // ~5.5px per char at font-size 9px
  const durationWidth = `${duration} DAYS`.length * 4.5  // ~4.5px per char at font-size 8px
  const maxTextWidth = Math.max(labelWidth, durationWidth)
  const requiredWidth = maxTextWidth + 24  // Add padding (12px each side)

  // Bookmark width is at least anchor width, extends only if text needs more space
  return Math.max(anchorWidth, Math.min(requiredWidth, 200))  // Cap at 200px
}

/**
 * Calculate task bookmark width based on text content + notes
 * Tasks need more space than phases due to notes field
 * @param {string} label - Task label text
 * @param {number} duration - Duration in days
 * @param {string} notes - Task notes (optional)
 * @param {number} anchorWidth - Anchor width (original task rect width)
 * @returns {number} Calculated bookmark width (max of anchor width or text width, capped at 300px)
 */
export const calculateTaskBookmarkWidth = (label, duration, notes, anchorWidth) => {
  // Estimate based on text length
  const labelWidth = label.length * 5.5  // ~5.5px per char at font-size 8px
  const durationWidth = `${duration} days`.length * 4.5  // ~4.5px per char at font-size 7px
  const notesWidth = notes ? Math.min(notes.length * 4.5, 200) : 0  // Cap notes contribution
  const maxTextWidth = Math.max(labelWidth, durationWidth, notesWidth)
  const requiredWidth = maxTextWidth + 24  // Add padding (12px each side)

  // Bookmark width is at least anchor width, extends only if text needs more space
  return Math.max(anchorWidth, Math.min(requiredWidth, 300))  // Cap at 300px (vs 200px for phases)
}
